import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { ImageField } from "./ImageField";

const inputCls = "mt-2 w-full border border-white/15 bg-ink px-3 py-2.5 text-white placeholder:text-zinc-500 focus:border-volt focus:outline-none";

const blank = (fields) => Object.fromEntries(fields.map((f) => [f.name, f.default ?? (f.nullable ? "" : f.type === "number" ? 0 : "")]));

const toPayload = (fields, v) => Object.fromEntries(fields.map((f) => {
  if (f.type !== "number") return [f.name, v[f.name] ?? ""];
  const raw = v[f.name];
  if (raw === "" || raw == null) return [f.name, f.nullable ? null : 0];
  return [f.name, Number(raw)];
}));

const FieldInput = ({ f, value, onChange, testId }) => {
  if (f.type === "image") return <ImageField value={value} onChange={onChange} keepPng={f.keepPng} testId={testId} />;
  if (f.type === "textarea") return <textarea rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />;
  if (f.type === "select") {
    return (
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId}>
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return <input type={f.type} required={f.required} value={value ?? ""} placeholder={f.placeholder} step={f.type === "number" ? "any" : undefined}
    onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />;
};

export const CrudPanel = ({ resource }) => {
  const { key, title, singular, fields, summary, thumb } = resource;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/${key}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setValues(blank(fields)); setEditing("new"); };
  const openEdit = (it) => { setValues({ ...blank(fields), ...Object.fromEntries(fields.map((f) => [f.name, it[f.name] ?? ""])) }); setEditing(it.id); };

  const save = async (e) => {
    e.preventDefault();
    const missing = fields.find((f) => f.required && !values[f.name]);
    if (missing) { toast.error(`Campo obbligatorio: ${missing.label}`); return; }
    setSaving(true);
    try {
      const payload = toPayload(fields, values);
      if (editing === "new") await api.post(`/${key}`, payload);
      else await api.put(`/${key}/${editing}`, payload);
      toast.success("Salvato");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm("Eliminare questo elemento?")) return;
    try {
      await api.delete(`/${key}/${it.id}`);
      toast.success("Eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section data-testid={`admin-panel-${key}`}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-4xl font-black uppercase">{title}</h2>
        <button onClick={openNew} data-testid={`admin-${key}-add-btn`}
          className="flex items-center gap-2 bg-trident px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-widest text-white hover:opacity-90">
          <Plus className="h-4 w-4" /> Aggiungi
        </button>
      </div>
      <div className="mt-6 divide-y divide-white/10 border border-white/10">
        {loading && <p className="flex items-center gap-2 p-6 text-zinc-400"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</p>}
        {!loading && items.length === 0 && <p className="p-6 text-zinc-400" data-testid={`admin-${key}-empty`}>Nessun elemento. Clicca "Aggiungi" per iniziare.</p>}
        {items.map((it, i) => {
          const [a, b] = summary(it);
          const t = thumb?.(it);
          return (
            <div key={it.id} className="flex items-center gap-4 p-4" data-testid={`admin-${key}-row-${i}`}>
              {thumb && <div className="h-12 w-16 shrink-0 overflow-hidden bg-surface">{t && <img src={t} alt="" className="h-full w-full object-cover" />}</div>}
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-white">{a}</p>
                <p className="truncate font-mono text-xs text-zinc-400">{b}{it.sample ? " · esempio" : ""}</p>
              </div>
              <button onClick={() => openEdit(it)} aria-label="Modifica" data-testid={`admin-${key}-edit-${i}`} className="grid h-9 w-9 place-items-center border border-white/15 hover:border-volt hover:text-volt"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(it)} aria-label="Elimina" data-testid={`admin-${key}-delete-${i}`} className="grid h-9 w-9 place-items-center border border-white/15 hover:border-flag hover:text-flag"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>

      <Dialog open={editing != null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto border-white/10 bg-surface" data-testid={`admin-${key}-dialog`}>
          <DialogTitle className="font-display text-3xl font-black uppercase">{editing === "new" ? `Nuovo ${singular}` : `Modifica ${singular}`}</DialogTitle>
          <form onSubmit={save} className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <label key={f.name} className={f.type === "textarea" || f.type === "image" || f.name === "title" ? "sm:col-span-2" : ""}>
                <span className="label-mono text-zinc-400">{f.label}{f.required ? " *" : ""}</span>
                <FieldInput f={f} value={values[f.name]} onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} testId={`admin-${key}-field-${f.name}`} />
              </label>
            ))}
            <button type="submit" disabled={saving} data-testid={`admin-${key}-save-btn`}
              className="mt-2 flex items-center justify-center gap-2 bg-trident py-3 font-mono text-xs font-bold uppercase tracking-widest text-white sm:col-span-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
